import { Capacitor } from "@capacitor/core";

type StraivAudioMessage =
  | { prepare: true }
  | { beginDucking: true }
  | { endDucking: true }
  | { playDuckedAudio: true; base64: string; mime?: string }
  | { active: boolean; title?: string }
  | { prepareBackgroundKeepAlive: true }
  | { resumeMusic: true };

let pendingAudioResolve: (() => void) | null = null;

declare global {
  interface Window {
    __straivAudioDone?: () => void;
  }
}

if (typeof window !== "undefined") {
  window.__straivAudioDone = () => {
    pendingAudioResolve?.();
    pendingAudioResolve = null;
  };
}

function postNative(message: StraivAudioMessage): void {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "ios") return;
  try {
    const w = window as Window & {
      webkit?: {
        messageHandlers?: { straivNowPlaying?: { postMessage: (m: StraivAudioMessage) => void } };
      };
    };
    w.webkit?.messageHandlers?.straivNowPlaying?.postMessage(message);
  } catch {
    // ignore
  }
}

/** Ambient + mixWithOthers before countdown/TTS — never steals music. */
export const prepareAudioForRun = () => {
  postNative({ prepare: true });
};

/** Lower other apps' volume while coach audio plays (voice-prompt session). */
export const beginAudioDucking = () => {
  postNative({ beginDucking: true });
};

/** Restore music volume after coach audio finishes. */
export const endAudioDucking = () => {
  postNative({ endDucking: true });
};

/**
 * Play coach audio natively with voice-prompt ducking (matches robot TTS behaviour).
 * Avoids WKWebView HTMLAudioElement which can pause Spotify/Apple Music.
 */
export function playDuckedNativeAudio(base64: string, mime = "audio/mpeg"): Promise<void> {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "ios") {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    pendingAudioResolve = resolve;
    postNative({ playDuckedAudio: true, base64, mime });
    // Safety timeout if native callback never fires
    window.setTimeout(() => {
      if (pendingAudioResolve === resolve) {
        pendingAudioResolve = null;
        endAudioDucking();
        resolve();
      }
    }, 120_000);
  });
}

export const setStraivNowPlaying = (_active: boolean, _title?: string) => {
  // no-op
};

export const resumeMusicAfterRunStart = () => {
  // no-op
};

export const prepareBackgroundKeepAlive = () => {
  if (Capacitor.getPlatform() === "ios") return;
  postNative({ prepareBackgroundKeepAlive: true });
};
